import {
	addDays,
	addMinutes,
	addMonths,
	addWeeks,
	addYears,
	differenceInMinutes,
	eachDayOfInterval,
	endOfDay,
	endOfMonth,
	endOfWeek,
	endOfYear,
	format,
	isSameDay,
	isSameMonth,
	isSameWeek,
	isSameYear,
	isValid,
	parseISO,
	startOfDay,
	startOfMonth,
	startOfWeek,
	startOfYear,
	subDays,
	subMonths,
	subWeeks,
	subYears,
} from "date-fns";
import type { ICalendarCell, IEvent } from "@/components/admin/calendar/interfaces";
import type { TCalendarView } from "@/components/admin/calendar/types";
import type { AppointmentsColorOptions } from "@/types/pocketbase";

const FORMAT_STRING = "MMM d, yyyy";
// Time constants
const MINUTES_IN_DAY = 1440; // 24 hours * 60 minutes

// Percentage constants
const FULL_WIDTH_PERCENT = 100;
const FULL_HEIGHT_PERCENT = 100;

// Calendar layout constants
const DAYS_IN_WEEK = 7;
const MAX_EVENT_POSITIONS_PER_DAY = 3; // Maximum events that can be displayed in a single day cell

export function getDateRange(view: TCalendarView, date: Date) {
	let startDate: Date;
	let endDate: Date;

	switch (view) {
		case "month":
			startDate = startOfWeek(startOfMonth(date));
			endDate = endOfWeek(endOfMonth(date));
			break;
		case "week":
			startDate = startOfWeek(date);
			endDate = endOfWeek(date);
			break;
		case "day":
			startDate = startOfWeek(startOfMonth(date));
			endDate = endOfWeek(endOfMonth(date));
			break;
		case "year":
			startDate = startOfWeek(startOfYear(date));
			endDate = endOfWeek(endOfYear(date));
			break;
		case "agenda":
			startDate = startOfMonth(date);
			endDate = endOfMonth(date);
			break;
		default:
			startDate = startOfDay(date);
			endDate = endOfDay(date);
	}

	return { startDate, endDate };
}

export function rangeText(view: TCalendarView, date: Date): string {
	if (view === "day") {
		return format(date, FORMAT_STRING);
	}

	const { startDate, endDate } = getDateRange(view, date);
	return `${format(startDate, FORMAT_STRING)} - ${format(endDate, FORMAT_STRING)}`;
}

export function navigateDate(date: Date, view: TCalendarView, direction: "previous" | "next"): Date {
	const operations: Record<TCalendarView, (d: Date, n: number) => Date> = {
		month: direction === "next" ? addMonths : subMonths,
		week: direction === "next" ? addWeeks : subWeeks,
		day: direction === "next" ? addDays : subDays,
		year: direction === "next" ? addYears : subYears,
		agenda: direction === "next" ? addMonths : subMonths,
	};

	return operations[view](date, 1);
}

export function getEventsCount(events: Array<IEvent>, date: Date, view: TCalendarView): number {
	const compareFns: Record<TCalendarView, (d1: Date, d2: Date) => boolean> = {
		day: isSameDay,
		week: isSameWeek,
		month: isSameMonth,
		year: isSameYear,
		agenda: isSameMonth,
	};

	const compareFn = compareFns[view];
	return events.filter((event) => compareFn(parseISO(event.startDate), date)).length;
}

export function groupEvents(dayEvents: Array<IEvent>): Array<Array<IEvent>> {
	const sortedEvents = dayEvents.sort((a, b) => parseISO(a.startDate).getTime() - parseISO(b.startDate).getTime());
	const groups: Array<Array<IEvent>> = [];

	for (const event of sortedEvents) {
		const eventStart = parseISO(event.startDate);
		let placed = false;

		for (const group of groups) {
			const lastEventInGroup = group.at(-1);
			if (!lastEventInGroup) {
				continue;
			}

			const lastEventEnd = addMinutes(parseISO(lastEventInGroup.startDate), lastEventInGroup.duration);

			if (eventStart >= lastEventEnd) {
				group.push(event);
				placed = true;
				break;
			}
		}

		if (!placed) {
			groups.push([event]);
		}
	}

	return groups;
}

export function getEventBlockStyle(event: IEvent, day: Date, groupIndex: number, groupSize: number) {
	const startDate = parseISO(event.startDate);
	const dayStart = startOfDay(day); // Use startOfDay instead of manual reset
	const eventStart = startDate < dayStart ? dayStart : startDate;
	const startMinutes = differenceInMinutes(eventStart, dayStart);

	const top = (startMinutes / MINUTES_IN_DAY) * FULL_HEIGHT_PERCENT; // 1440 minutes in a day
	const width = FULL_WIDTH_PERCENT / groupSize;
	const left = groupIndex * width;

	return { top: `${top}%`, width: `${width}%`, left: `${left}%` };
}

export function getCalendarCells(selectedDate: Date): Array<ICalendarCell> {
	const year = selectedDate.getFullYear();
	const month = selectedDate.getMonth();

	const daysInMonth = endOfMonth(selectedDate).getDate(); // Faster than new Date(year, month + 1, 0)
	const firstDayOfMonth = startOfMonth(selectedDate).getDay();
	const daysInPrevMonth = endOfMonth(new Date(year, month - 1)).getDate();
	const totalDays = firstDayOfMonth + daysInMonth;

	const prevMonthCells = Array.from({ length: firstDayOfMonth }, (_, i) => ({
		day: daysInPrevMonth - firstDayOfMonth + i + 1,
		currentMonth: false,
		date: new Date(year, month - 1, daysInPrevMonth - firstDayOfMonth + i + 1),
	}));

	const currentMonthCells = Array.from({ length: daysInMonth }, (_, i) => ({
		day: i + 1,
		currentMonth: true,
		date: new Date(year, month, i + 1),
	}));

	const nextMonthCells = Array.from({ length: (DAYS_IN_WEEK - (totalDays % DAYS_IN_WEEK)) % DAYS_IN_WEEK }, (_, i) => ({
		day: i + 1,
		currentMonth: false,
		date: new Date(year, month + 1, i + 1),
	}));

	return [...prevMonthCells, ...currentMonthCells, ...nextMonthCells];
}

// Helper function to sort events by duration and start date
function sortEventsByDurationAndDate(multiDayEvents: Array<IEvent>, singleDayEvents: Array<IEvent>): Array<IEvent> {
	const sortedMultiDay = multiDayEvents.sort((a, b) => {
		return b.duration - a.duration || parseISO(a.startDate).getTime() - parseISO(b.startDate).getTime();
	});

	const sortedSingleDay = singleDayEvents.sort(
		(a, b) => parseISO(a.startDate).getTime() - parseISO(b.startDate).getTime()
	);

	return [...sortedMultiDay, ...sortedSingleDay];
}

// Helper function to find available position for an event
function findAvailablePosition(eventDays: Array<Date>, occupiedPositions: Record<string, Array<boolean>>): number {
	for (let i = 0; i < MAX_EVENT_POSITIONS_PER_DAY; i++) {
		if (
			eventDays.every((day) => {
				const dayPositions = occupiedPositions[startOfDay(day).toISOString()];
				return dayPositions && !dayPositions[i];
			})
		) {
			return i;
		}
	}
	return -1;
}

export function calculateMonthEventPositions(
	multiDayEvents: Array<IEvent>,
	singleDayEvents: Array<IEvent>,
	selectedDate: Date
): Record<string, number> {
	const { startDate, endDate } = getDateRange("month", selectedDate);

	const eventPositions: Record<string, number> = {};
	const occupiedPositions: Record<string, Array<boolean>> = {};

	// Initialize occupied positions for each day
	for (const day of eachDayOfInterval({ start: startDate, end: endDate })) {
		occupiedPositions[day.toISOString()] = [false, false, false];
	}

	const sortedEvents = sortEventsByDurationAndDate(multiDayEvents, singleDayEvents);

	for (const event of sortedEvents) {
		const eventStart = parseISO(event.startDate);
		const eventEnd = addMinutes(eventStart, event.duration);
		const eventDays = eachDayOfInterval({
			start: eventStart < startDate ? startDate : eventStart,
			end: eventEnd > endDate ? endDate : eventEnd,
		});

		const position = findAvailablePosition(eventDays, occupiedPositions);

		if (position !== -1) {
			for (const day of eventDays) {
				const dayKey = startOfDay(day).toISOString();
				if (!occupiedPositions[dayKey]) continue;
				occupiedPositions[dayKey][position] = true;
			}
			eventPositions[event.id] = position;
		}
	}

	return eventPositions;
}

export function getMonthCellEvents(date: Date, events: Array<IEvent>, eventPositions: Record<string, number>) {
	const dayStart = startOfDay(date);
	const eventsForDate = events.filter((event) => {
		const eventStart = parseISO(event.startDate);
		const eventEnd = addMinutes(eventStart, event.duration);
		return (
			(dayStart >= eventStart && dayStart <= eventEnd) ||
			isSameDay(dayStart, eventStart) ||
			isSameDay(dayStart, eventEnd)
		);
	});

	return eventsForDate
		.map((event) => {
			const eventStart = parseISO(event.startDate);
			const eventEnd = addMinutes(eventStart, event.duration);
			const isMultiDay = isSameDay(eventStart, eventEnd);
			return {
				...event,
				position: eventPositions[event.id] ?? -1,
				isMultiDay,
			};
		})
		.sort((a, b) => {
			if (a.isMultiDay && !b.isMultiDay) {
				return -1;
			}
			if (!a.isMultiDay && b.isMultiDay) {
				return 1;
			}
			return a.position - b.position;
		});
}

export function formatTime(date: Date | string, use24HourFormat: boolean): string {
	const parsedDate = typeof date === "string" ? parseISO(date) : date;
	if (!isValid(parsedDate)) {
		return "";
	}
	return format(parsedDate, use24HourFormat ? "HH:mm" : "h:mm a");
}

export const getFirstLetters = (str: string): string => {
	if (!str) return "";

	const words = str.split(" ");
	if (words.length === 1 && words[0]) {
		return words[0].charAt(0).toUpperCase();
	}

	return `${words[0]?.charAt(0).toUpperCase()}${words[1]?.charAt(0).toUpperCase()}`;
};

export const getColorClass = (color: AppointmentsColorOptions): string => {
	const colorClasses: Record<AppointmentsColorOptions, string> = {
		red: "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300",
		yellow:
			"border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-300",
		green: "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300",
		blue: "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300",
		orange:
			"border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-300",
		purple:
			"border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-800 dark:bg-purple-950 dark:text-purple-300",
	};
	return colorClasses[color] || "";
};

export const getBgColor = (color: AppointmentsColorOptions): string => {
	const colorClasses: Record<AppointmentsColorOptions, string> = {
		red: "bg-red-400 dark:bg-red-600",
		yellow: "bg-yellow-400 dark:bg-yellow-600",
		green: "bg-green-400 dark:bg-green-600",
		blue: "bg-blue-400 dark:bg-blue-600",
		orange: "bg-orange-400 dark:bg-orange-600",
		purple: "bg-purple-400 dark:bg-purple-600",
	};
	return colorClasses[color] || "";
};

export const getSwatchColor = (color: AppointmentsColorOptions): string => {
	const colorClasses: Record<AppointmentsColorOptions, string> = {
		red: "bg-red-600 dark:bg-red-700",
		yellow: "bg-yellow-600 dark:bg-yellow-700",
		green: "bg-green-600 dark:bg-green-700",
		blue: "bg-blue-600 dark:bg-blue-700",
		orange: "bg-orange-600 dark:bg-orange-700",
		purple: "bg-purple-600 dark:bg-purple-700",
	};
	return colorClasses[color] || "";
};

export const toCapitalize = (str: string): string => {
	if (!str) {
		return "";
	}
	return str.charAt(0).toUpperCase() + str.slice(1);
};
